import { FC, useState } from 'react';
import {
  Root,
  Trigger,
  Portal,
  Content,
  Close,
  Overlay,
  DialogTitle,
} from '@radix-ui/react-dialog';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Button, EditableString } from '../../components';
import { ButtonColor, ButtonSize, Icons } from '../../enums';
import strings from '../../language/Swedish';
import About from './components/About';
import Data from './components/Data';
import { useFilterContext } from '../../context/FilterContext';
import { useProjectContext } from '../../context/ProjectContext';
import { useMapContext } from '../../context/MapContext';
import StaticMapImg from '@/helpers/StaticMap';
import DownLoadReportButton from '@/pages/report/DownLoadReportButton';
const StyledOverlay = styled(Overlay)`
  ${tw`z-50 flex fixed p-4 sm:p-8 inset-0 place-items-center bg-slate-800/75 backdrop-blur-sm`}
`;

const StyledContent = styled(Content)`
  ${tw`w-full flex-1 max-w-[800px] overflow-y-scroll m-auto max-h-full rounded-2xl p-6 border border-white bg-white/90 backdrop-blur shadow-2xl sm:p-8 sm:w-[90dvw] sm:max-w-5xl lg:max-w-4xl`}

  &:focus {
    ${tw`outline-none`}
  }
`;

const CloseButton = styled.div`
  ${tw`absolute top-1 right-1 rounded-full`}
`;

const AboutWrapper = styled.div`
  ${tw`flex flex-col items-stretch mt-4 mb-6 gap-6 md:flex-row`}
`;

const MapContainer = styled.div`
  ${tw`flex-1 rounded-2xl bg-white/40 flex flex-col`}
`;

const DataWrapper = styled.div`
  ${tw`mb-6`}
`;

const BottomWrapper = styled.div`
  ${tw`flex justify-end gap-2`}
`;

const TextAreaLabel = styled.label`
  ${tw`block mt-4 mb-2 text-sm font-medium text-slate-800`}
`;

const TextArea = styled.textarea`
  ${tw`block p-2.5 w-full text-sm text-slate-800 bg-slate-50 rounded-lg border border-slate-300 shadow-slate-300 outline-none border-primary`}

  margin-top: 1rem !important;

  &:focus: {
    ${tw`shadow-[0_0_0_4px_rgb(var(--color-primary)_/_0.2)]`}
  }
`;

const Hide = styled.div`
  ${tw`hidden`}
`;

const StyledMap = styled.div`
  ${tw`flex relative h-full w-full rounded-xl`}

  .googleMap {
    ${tw`rounded-xl`}
  }

  .gm-style-mtc,
  .gm-style-cc,
  .gm-control-active,
  .gm-control-zoom-out {
    ${tw`hidden`}
  }
`;

interface Props {
  save: (force: boolean) => void;
}

const Report: FC<Props> = ({ save }) => {
  const [comment, setComment] = useState<string>('');
  const [showRefPrice, setShowRefPrice] = useState<boolean>(true);

  const { project, setProject } = useProjectContext();
  const {
    soldData: { refData },
  } = useFilterContext();

  const { currentCoordinates, soldMarkerObjects } = useMapContext();
  const {
    project: { referencePrice },
  } = useProjectContext();


  const selectedMarkers =
    project.soldTableIds.selected && project.soldTableIds.selected.size > 0
      ? soldMarkerObjects?.filter(({ id }) =>
        project?.soldTableIds?.selected?.has(id)
      )
      : soldMarkerObjects;

  return (
    <Root>
      <Hide>
        <DialogTitle />
      </Hide>
      <Trigger asChild>
        <Button
          color={ButtonColor.primary}
          size={ButtonSize.large}
          icon={Icons.report}
          collapseInMobile
        >
          {strings.dashboard.report}
        </Button>
      </Trigger>
      <Portal>
        {/* To prevent sideeffect from radix */}
        <StyledOverlay style={{ touchAction: "none" }}>
          <StyledContent aria-describedby="">
            <EditableString
              text={project.name || ''}
              handleSave={(value) => {
                setProject((prevState) => ({
                  ...prevState,
                  name: value,
                }));
              }}
            />
            <AboutWrapper>
              <About
                setShowRefPrice={() =>
                  setShowRefPrice((prevState) => !prevState)
                }
                showRefPrice={showRefPrice}
                refData={refData}
                referencePrice={referencePrice}
                project={project}>
                <>
                  <TextAreaLabel>{strings.report.comment}</TextAreaLabel>
                  <TextArea
                    id="message"
                    value={comment}
                    onChange={(e) => {
                      setComment(e.target.value);
                    }}
                  />
                </>
              </About>
              <MapContainer>
                <StyledMap>
                  <img
                    src={StaticMapImg({
                      markers: selectedMarkers,
                      coordinatesSetting: currentCoordinates,
                    })}
                  />
                </StyledMap>
              </MapContainer>
            </AboutWrapper>
            <DataWrapper>
              <Data />
            </DataWrapper>
            <BottomWrapper>
              <DownLoadReportButton
                showRefPrice={showRefPrice}
                reportComment={comment}
                save={save}
              />
            </BottomWrapper>
            <Close asChild>
              <CloseButton>
                <Button
                  icon={Icons.cross}
                  color={ButtonColor.transparent}
                  size={ButtonSize.large}
                />
              </CloseButton>
            </Close>
          </StyledContent>
        </StyledOverlay>
      </Portal>
    </Root>
  );
};

export default Report;
